'use client'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { clsx } from 'clsx'
import { Keyboard, Navigation, Pagination, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'
import { Icon } from '@/components/Icon'
import { Image } from '@/components/Image'
import { ConsentContext } from '@/context/ConsentContext'
import { useIsPurMode } from '@/hooks/useIsPurMode'
import { pushAd, reloadAd } from '@/libs/AdAlliance/AdAlliance.lib'
import { ga4Event, ga4PushMetaData } from '@/libs/Google/GoogleTagManager.lib'
import { internalTracking, vgwortTracking } from '@/libs/Tracking/Tracking.lib'
import type { MetaData } from '@/types/cms'
import { toKebabCase } from '@/utils/string'
import styles from './Gallery.module.scss'
import AppContext from '@/types/context'
import { MetaDataContext } from '@/context/MetadataContext'
import { AreaItem } from '@/types/cms/AreaItem'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

declare global {
  interface Window {
    _ntv_adWrapperGal: HTMLElement | null
  }
}

const Gallery = ({
  context,
  item,
  variant,
}: {
  context: AppContext
  item: AreaItem
  variant?: 'inline' | 'inline-top'
}) => {
  const [grid, setGrid] = React.useState<string | undefined>(undefined)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [isBeginning, setIsBeginning] = useState<boolean>(true)
  const [isEnd, setIsEnd] = useState<boolean>(false)
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null)
  const gallery = item.items?.filter((child) => child.type === 'gallery')[0]
  const pictures = item.items?.filter((child) => child.type === 'picture')
  if (!item?.items || !gallery || !pictures) {
    return
  }
  const ADID = 'biga_1'
  const ADCOUNT = 3
  const adwrapperWinName = '_ntv_adWrapperGal'

  const consentContext = useContext(ConsentContext)
  const metaData: MetaData | undefined = useContext(MetaDataContext)

  const hasTrackedInitial = useRef<boolean>(false)
  const slideTimeout = useRef<number | null>(null)

  const isPurMode = useIsPurMode()

  const [adWrapper, setAdWrapper] = useState<HTMLElement | undefined>(undefined)

  const updateNavigationState = useCallback((swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex)
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }, [])

  const isAdAllowed = () => !metaData?.model.adsDisable && !isPurMode

  const showAd = (index: number) => isAdAllowed() && index !== 0 && index % ADCOUNT === 0

  const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0

  const isElemVisible = (elem: Element) =>
    getComputedStyle(elem).display !== 'none' && getComputedStyle(elem).display !== 'hidden'

  const createAdWrapper = (adSlide: Element) => {
    const adWrapper = document.createElement('div')
    adWrapper.className = styles.adwrapper

    // hack for not bubbeling touch events because of iframe
    if (isTouchDevice()) {
      const adOverlay = document.createElement('div')
      adOverlay.className = styles['ad-overlay']

      adOverlay.addEventListener('pointerdown', function () {
        adOverlay.style.display = 'none'
      })

      adOverlay.addEventListener('pointerup', function () {
        setTimeout(() => {
          adOverlay.style.display = 'block'
        }, 50)
      })
      adWrapper.appendChild(adOverlay)
    }

    const adslot = document.createElement('div')
    adslot.id = ADID
    adslot.className = 'gujAd'

    adWrapper.appendChild(adslot)
    window[adwrapperWinName] = adWrapper

    adSlide.appendChild(adWrapper)

    pushAd(ADID)

    return adWrapper
  }

  const trackGallery = useCallback(
    (index: number): void => {
      if (variant || !metaData) {
        return
      }

      try {
        if (!hasTrackedInitial.current) {
          ga4PushMetaData({
            trackingPath: metaData.model.trackingPath,
            article: metaData.model.article,
            sectionName: metaData.model.sectionName,
            isWebView: context.webview,
            gtmKeywords: metaData.model.gtmKeywords,
          })
        }

        ga4Event({
          eventname: 'gallery_slide',
          event: 'gallery_slide',
          slide_number: index,
          content_group: 'Mediathek-Bilderserien',
          content_ressort: metaData.model.sectionName,
          page_location: metaData.model.canonicalUrl,
          page_title: metaData.model.title,
        })

        internalTracking(metaData.model.internalTrackingUrl)
      } catch (error) {
        console.error('Gallery tracking failed', error)
      }
    },
    [metaData],
  )

  useEffect(() => {
    if (hasTrackedInitial.current) {
      return
    }

    trackGallery(1)
    hasTrackedInitial.current = true

    if (
      !variant &&
      metaData?.model.vgwortTrackingUrl &&
      consentContext.consentReady &&
      consentContext.userConsents?.vgwort
    ) {
      vgwortTracking(metaData.model.vgwortTrackingUrl)
    }
  }, [consentContext, trackGallery])

  useEffect(
    () => () => {
      if (slideTimeout.current !== null) {
        window.clearTimeout(slideTimeout.current)
      }
    },
    [],
  )

  const prevRef = React.useRef(null)
  const nextRef = React.useRef(null)
  const paginationRef = React.useRef(null)

  useEffect(() => {
    if (!swiperInstance || ['xs', 's', 'm'].includes(grid as string)) {
      return
    }

    const navigationParams =
      typeof swiperInstance.params.navigation === 'object' ? swiperInstance.params.navigation : {}

    swiperInstance.params.navigation = {
      ...navigationParams,
      prevEl: prevRef.current,
      nextEl: nextRef.current,
    }

    if (swiperInstance.navigation) {
      swiperInstance.navigation.destroy()
      swiperInstance.navigation.init()
      swiperInstance.navigation.update()
      updateNavigationState(swiperInstance)
    }
  }, [grid, swiperInstance, updateNavigationState])

  useEffect(() => {
    const element = document.documentElement

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry.contentRect.width <= 743) {
        setGrid('xs')
      } else if (entry.contentRect.width <= 903) {
        setGrid('s')
      } else if (entry.contentRect.width <= 1235) {
        setGrid('m')
      } else if (entry.contentRect.width <= 1375) {
        setGrid('l')
      } else if (entry.contentRect.width <= 1799) {
        setGrid('xl')
      } else {
        setGrid('xxl')
      }
    })

    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  const amountOfAds = isAdAllowed() ? Math.floor(((pictures.length ?? 0) - 1) / ADCOUNT) : 0

  return (
    <section className={clsx(styles.section, variant === 'inline' && styles['section-inline'])}>
      <div
        className={clsx(
          styles.slider,
          {
            [styles.loading]: !isInitialized,
          },
          variant === 'inline' && styles['slider-inline'],
        )}
      >
        <Swiper
          onSwiper={(swiper) => setSwiperInstance(swiper)}
          slidesPerView={1}
          navigation={
            !['xs', 's', 'm'].includes(grid as string) && {
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }
          }
          modules={[Navigation, Virtual, Keyboard, Pagination]}
          threshold={isInitialized ? 10 : 50}
          resistance={isInitialized}
          resistanceRatio={isInitialized ? 0.85 : 0}
          virtual={{
            enabled: true,
            addSlidesBefore: ADCOUNT + 2,
            addSlidesAfter: ADCOUNT + 2,
          }}
          className={styles.swiper}
          a11y={{
            enabled: true,
            prevSlideMessage: 'Vorheriges Bild',
            nextSlideMessage: 'Nächstes Bild',
          }}
          onSlideChange={(swiper) => updateNavigationState(swiper)}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          onInit={(swiper) => {
            setIsInitialized(true)
            updateNavigationState(swiper)

            setTimeout(() => {
              const firstAdSlide = swiper.el?.querySelector('[data-adslide]')
              if (firstAdSlide) {
                const adWrapper = window[adwrapperWinName] || createAdWrapper(firstAdSlide)
                setAdWrapper(adWrapper)
              }
            }, 150)
          }}
          onRealIndexChange={(swiper) => {
            if (!isInitialized) return

            const getVirtualIndex = (realOffset: number) => {
              const index = swiper.realIndex + realOffset
              if (index < 0 || index >= swiper.virtual.slides.length) return undefined
              return index - swiper.virtual.from
            }

            const activeIdx = swiper.realIndex - swiper.virtual.from
            const activeSlide = swiper.slides[activeIdx]

            if (activeSlide?.dataset.count) {
              trackGallery(Number.parseInt(activeSlide.dataset.count))
            }

            const prevVirtualIdx = swiper.previousIndex - swiper.virtual.from
            if (swiper.slides[prevVirtualIdx]?.dataset.adslide) {
              reloadAd(ADID)
            }

            const nearAdSlide = [-2, -1, 1, 2]
              .map((offset) => getVirtualIndex(offset))
              .filter((idx): idx is number => idx !== undefined) // remove undefined
              .map((idx) => swiper.slides[idx])
              .find((slide) => slide?.dataset.adslide)

            if (nearAdSlide && adWrapper && !nearAdSlide.querySelector(`#${ADID}`)) {
              nearAdSlide.appendChild(adWrapper)
            }
          }}
          onSlideChangeTransitionEnd={(swiper) => {
            const activeIdx = swiper.realIndex - swiper.virtual.from
            const activeSlide = swiper.slides[activeIdx]
            const actualAdSlot = activeSlide?.querySelector(`#${ADID}`)
            if (actualAdSlot && !isElemVisible(actualAdSlot)) {
              if (swiper.previousIndex < swiper.realIndex) {
                swiper.slideNext()
              } else {
                swiper.slidePrev()
              }
            }
          }}
        >
          {pictures?.map((child: any, index: number) => {
            const image = child.model.image
            if (!image) {
              return null
            }

            const caption = child.model.imageAttributes?.title ?? ''
            const shouldHideCaption = /\.(jpe?g|png|webp)$/i.test(caption.trim())
            const slideId = child.model.id ?? toKebabCase(child.model.title)

            return (
              <>
                {showAd(index) && (
                  <SwiperSlide
                    className={clsx(styles.slide, variant === 'inline' && styles['inline-slide'])}
                    key={'adslide-' + slideId}
                    data-adslide="true"
                    data-count={index + 1}
                  ></SwiperSlide>
                )}
                <SwiperSlide
                  key={slideId}
                  className={clsx(styles.slide, variant === 'inline' && styles['inline-slide'])}
                  data-count={index + 1}
                >
                  <figure className={styles.figure}>
                    <div className={styles.image}>
                      <Image image={image} alt={image.filename || child.model.title} priority isOriginalSize noCrop />
                    </div>
                    <figcaption className={clsx(styles.caption, variant === 'inline' && styles['inline-caption'])}>
                      <span className={styles['caption-text']}>{!shouldHideCaption && caption}</span>
                      <span className={styles.copyright}>{child.model.imageAttributes?.credit}</span>
                    </figcaption>
                  </figure>
                </SwiperSlide>
              </>
            )
          })}
        </Swiper>
      </div>
      <div
        className={clsx(
          styles.navigation,
          variant && ['inline', 'inline-top'].includes(variant) && styles['inline-navigation'],
        )}
      >
        {grid && !['xs', 's', 'm'].includes(grid as string) && (
          <button
            ref={prevRef}
            className={clsx(styles['navigation-btn'], isBeginning && styles['navigation-btn-hidden'])}
            disabled={isBeginning}
            aria-hidden={isBeginning}
            tabIndex={isBeginning ? -1 : 0}
          >
            <Icon name="arrowLeft" size={24} />
          </button>
        )}
        <div className={styles.pagination} ref={paginationRef}>
          <span
            className={clsx(
              styles['pagination-bullet'],
              styles['pagination-bullet-prev-prev'],
              activeIndex < 2 && styles['pagination-bullet-hidden'],
            )}
          />
          <span
            className={clsx(
              styles['pagination-bullet'],
              styles['pagination-bullet-prev'],
              activeIndex < 1 && styles['pagination-bullet-hidden'],
            )}
          />
          <span className={clsx(styles['pagination-bullet'], styles['pagination-bullet-active'])} />
          <span
            className={clsx(
              styles['pagination-bullet'],
              styles['pagination-bullet-next'],
              !(activeIndex < (pictures?.length ?? 0)) && styles['pagination-bullet-hidden'],
            )}
          />
          <span
            className={clsx(
              styles['pagination-bullet'],
              styles['pagination-bullet-next-next'],
              !(activeIndex < (pictures?.length ?? 1) - 1) && styles['pagination-bullet-hidden'],
            )}
          />
        </div>
        {grid && !['xs', 's', 'm'].includes(grid as string) && (
          <button
            ref={nextRef}
            className={clsx(styles['navigation-btn'], isEnd && styles['navigation-btn-hidden'])}
            disabled={isEnd}
            aria-hidden={isEnd}
            tabIndex={isEnd ? -1 : 0}
          >
            <Icon name="arrowRight" size={24} />
          </button>
        )}
        <div className={styles.status}>
          <span className={styles.index}>{activeIndex + 1}</span> / {(pictures?.length ?? 0) + amountOfAds}
        </div>
      </div>
    </section>
  )
}

export default Gallery
